import classNames from 'clsx'
import { bool, func, number, oneOf, shape, string } from 'prop-types'

import { PAGE_TYPES } from 'shared/consts'
import { withI18n } from 'shared/utils/withI18n'
import Form from 'views/components/atoms/Form'
import IconSearch from 'views/components/atoms/Icon/IconSearch'
import InputWithButton from 'views/components/molecules/InputWithButton'
import PreQuerySuggestions from 'views/components/organisms/PreQuerySuggestions'
import { shouldRenderPqsl } from 'views/components/organisms/PreQuerySuggestions/helpers'
import { getPlaceholderText } from 'views/components/organisms/SearchBox/helper.ts'
import SearchBoxList from 'views/components/organisms/SearchBox/SearchBoxList'
import SearchHistory from 'views/components/organisms/SearchBox/SearchHistory'
import { i18nType, publicRuntimeConfigType } from 'views/utils/propTypes'

import './SearchBoxDesktop.scss'

const SearchBoxDesktop = ({
  publicRuntimeConfig: {
    publicConfig: { tenant },
  },
  i18n,
  query,
  inputId,
  displayResults,
  handleInputUpdate,
  handleClear,
  handleClearHistory,
  handleKeyUp,
  handleSubmit,
  focusedProductIndex,
  focusedSuggestionIndex,
  displayedValue,
  formAction,
  handleShowAll,
  handleItemClick,
  hideResults,
  handleValueChange,
  searchHistory,
  pageType,
  products,
  redirectData,
  withHighlightedProduct,
  querySuggestions,
}) => {
  const handleQueryUpdate = queryToUpdate => {
    handleInputUpdate({ target: { value: queryToUpdate } })
  }

  const preQuerySuggestionsProps = {
    searchHistory,
    handleClearHistory,
    handleQueryUpdate,
    fromPreQuerySuggestions: true,
  }
  return (
    <div className="o-SearchBox o-searchBarInteraction">
      <Form
        action={formAction}
        name="search-box"
        method="get"
        data-clientside-hook="chargeUrlSearchParams"
        onSubmit={handleSubmit}
      >
        <InputWithButton
          type="search"
          name="query"
          buttonType="submit"
          data-clientside-hook="track-apply"
          buttonLabel={i18n._('general.button.label.search')}
          placeholder={getPlaceholderText({
            tenant,
            defaultPlaceholderText: i18n._('general.label.placeholder.search'),
            i18n,
          })}
          inputId={inputId}
          onChange={handleInputUpdate}
          onClear={handleClear}
          onKeyUp={handleKeyUp}
          value={displayedValue}
          icon={<IconSearch />}
          disableAutoCompleteAndFriends
          mediumViewIconOnly
          grayInputBg
        />
      </Form>

      <div
        className={classNames(`o-PreQuerySuggestLayer__wrapper`, {
          'u-hidden': query,
        })}
        aria-hidden="true"
        data-clientside-hook="pqsl_wrapper-desktop"
      >
        {shouldRenderPqsl(pageType) && (
          <PreQuerySuggestions
            {...{
              hasSearchHistory: Boolean(searchHistory?.length),
              query,
            }}
          >
            <SearchHistory {...preQuerySuggestionsProps} />
          </PreQuerySuggestions>
        )}
      </div>

      <SearchBoxList
        query={query}
        displayResults={displayResults}
        products={products}
        redirectData={redirectData}
        focusedProductIndex={focusedProductIndex}
        focusedSuggestionIndex={focusedSuggestionIndex}
        formAction={formAction}
        handleShowAll={handleShowAll}
        handleItemClick={handleItemClick}
        hideResults={hideResults}
        handleValueChange={handleValueChange}
        withHighlightedProduct={withHighlightedProduct}
        querySuggestions={querySuggestions}
      />
    </div>
  )
}

SearchBoxDesktop.propTypes = {
  ...SearchHistory.propTypes,
  i18n: i18nType.isRequired,
  // comes from SearchBoxContainer
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  query: SearchBoxList.propTypes.query,
  inputId: string.isRequired,
  formAction: string.isRequired,
  displayResults: SearchBoxList.propTypes.displayResults,
  products: SearchBoxList.propTypes.products,
  redirectData: SearchBoxList.propTypes.redirectData,
  handleItemClick: SearchBoxList.propTypes.handleItemClick,
  handleInputUpdate: func,
  handleClearHistory: func,
  handleClear: func,
  handleKeyUp: func,
  handleSubmit: func,
  handleShowAll: func,
  focusedProductIndex: number,
  displayedValue: string,
  hideResults: func,
  handleValueChange: func,
  pageType: oneOf(PAGE_TYPES),
  searchBannerRef: shape({}),
  withHighlightedProduct: bool,
  querySuggestions: SearchBoxList.propTypes.querySuggestions,
}

SearchBoxDesktop.defaultProps = {
  ...SearchHistory.defaultProps,
  query: SearchBoxList.defaultProps.query,
  displayResults: SearchBoxList.defaultProps.displayResults,
  products: SearchBoxList.defaultProps.products,
  redirectData: SearchBoxList.defaultProps.redirectData,
  handleItemClick: SearchBoxList.defaultProps.handleItemClick,
  handleInputUpdate: null,
  handleClearHistory: null,
  handleClear: null,
  handleKeyUp: null,
  handleSubmit: null,
  handleShowAll: null,
  focusedProductIndex: -1,
  displayedValue: '',
  hideResults: null,
  handleValueChange: null,
  pageType: null,
  searchBannerRef: null,
  withHighlightedProduct: false,
  querySuggestions: SearchBoxList.defaultProps.querySuggestions,
}

export default withI18n()(SearchBoxDesktop)
